import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, MessageSquare, Car, DollarSign, TrendingUp, Star, Users, Gauge, Wallet, Megaphone, Trophy, Loader2, Target, Pencil, Save, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, icon: Icon, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-widest">{Icon && <Icon size={13} />}{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value ?? "—"}</div>
      {sub && <div className="text-white/40 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

const RANKS = [
  { key: "revenue", label: "Revenue" },
  { key: "bookings", label: "Bookings" },
  { key: "roi", label: "ROI" },
  { key: "conversion_rate", label: "Conversion" },
];

const GOAL_META = [
  { key: "fleet_size", label: "Fleet Size", fmt: (v) => Math.round(v) },
  { key: "monthly_revenue", label: "Monthly Revenue", fmt: (v) => `$${Number(v).toLocaleString()}` },
  { key: "monthly_bookings", label: "Bookings This Month", fmt: (v) => Math.round(v) },
  { key: "google_reviews", label: "Google Reviews", fmt: (v) => Math.round(v) },
  { key: "utilization_pct", label: "Fleet Utilization", fmt: (v) => `${v}%` },
  { key: "repeat_customer_pct", label: "Repeat Customers", fmt: (v) => `${v}%` },
];

function GrowthGoals({ token }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/marketing/goals`, H).then((r) => { setData(r.data); setDraft(r.data.targets); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const save = async () => {
    try {
      await axios.put(`${API}/marketing/goals`, Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, Number(v) || 0])), H);
      toast.success("Growth goals updated.");
      setEditing(false);
      load();
    } catch { toast.error("Could not save goals."); }
  };

  if (!data) return null;
  return (
    <div data-testid="growth-goals" className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Target size={17} className="text-[#F2B705]" />
        <h3 className="font-display font-bold text-white mr-auto">Growth Goals — road to #1 in St. Louis</h3>
        {editing ? (
          <button data-testid="goals-save-btn" onClick={save} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs"><Save size={13} /> Save</button>
        ) : (
          <button data-testid="goals-edit-btn" onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/60 text-xs"><Pencil size={12} /> Edit Targets</button>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-x-8 gap-y-4">
        {GOAL_META.map((g) => {
          const cur = data.current[g.key] ?? 0;
          const tgt = Number(editing ? draft[g.key] : data.targets[g.key]) || 1;
          const pct = Math.min(100, (cur / tgt) * 100);
          return (
            <div key={g.key} data-testid={`goal-${g.key}`}>
              <div className="flex items-baseline justify-between text-sm mb-1">
                <span className="text-white/70">{g.label}</span>
                <span className="text-white font-bold">{g.fmt(cur)} <span className="text-white/40 font-normal">/ {editing ? (
                  <input data-testid={`goal-input-${g.key}`} type="number" value={draft[g.key]} onChange={(e) => setDraft({ ...draft, [g.key]: e.target.value })}
                    className="w-20 bg-black border border-[#F2B705]/40 rounded px-1 py-0.5 text-white text-xs text-right" />
                ) : g.fmt(data.targets[g.key])}</span></span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-700 ${pct >= 100 ? "bg-green-400" : "bg-[#F2B705]"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="text-white/35 text-[11px] mt-0.5">{pct.toFixed(0)}% of goal</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReferralLeaderboard({ token }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/marketing/referral-leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data)).catch(() => {});
  }, [token]);
  return (
    <div data-testid="referral-leaderboard" className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
      <h3 className="font-display font-bold text-white flex items-center gap-2 mb-4"><Crown size={16} className="text-[#F2B705]" /> Top Referrers</h3>
      <div className="space-y-2.5">
        {rows.slice(0, 8).map((r, i) => (
          <div key={r.email} className="flex items-center gap-3">
            <span className={`w-6 text-center font-black ${i === 0 ? "text-[#F2B705]" : "text-white/40"}`}>{i + 1}</span>
            <div className="min-w-0 mr-auto">
              <div className="text-white text-sm font-semibold truncate">{r.name}</div>
              <div className="text-white/40 text-xs">{r.referrals} referral{r.referrals === 1 ? "" : "s"} · ${r.earned} earned</div>
            </div>
            {r.revenue_driven > 0 && <span className="text-green-400 text-xs font-semibold">${Number(r.revenue_driven).toLocaleString()} driven</span>}
          </div>
        ))}
        {rows.length === 0 && <div className="text-white/40 text-sm py-4 text-center">No referrals yet — codes go out automatically after each rental.</div>}
      </div>
    </div>
  );
}

export default function CeoDashboard({ token }) {
  const [d, setD] = useState(null);
  const [board, setBoard] = useState([]);
  const [rankBy, setRankBy] = useState("revenue");
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/marketing/dashboard`, H).then((r) => setD(r.data)).catch(() => {});
    axios.get(`${API}/marketing/scoreboard`, H).then((r) => setBoard(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading CEO Dashboard…</div>;

  const channel = (name) => d.leads_by_source.find((s) => s.source === name) || { leads: 0, booked: 0 };
  const ranked = [...board].sort((a, b) => (b[rankBy] ?? -Infinity) - (a[rankBy] ?? -Infinity));

  return (
    <div data-testid="ceo-dashboard" className="space-y-6">
      <GrowthGoals token={token} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-revenue-today" label="Revenue Today" value={`$${Number(d.revenue_today).toLocaleString()}`} icon={DollarSign} />
        <Stat testid="stat-revenue-week" label="Revenue This Week" value={`$${Number(d.revenue_week).toLocaleString()}`} icon={DollarSign} />
        <Stat testid="stat-active-rentals" label="Active Rentals" value={d.active_rentals} icon={Car} sub={`of ${d.vehicle_count} vehicles`} />
        <Stat testid="stat-utilization" label="Fleet Utilization" value={`${d.fleet_utilization}%`} icon={Gauge} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat testid="stat-leads-today" label="New Leads Today" value={d.leads_today} icon={Megaphone} sub={`${d.leads_new} uncontacted`} />
        <Stat testid="stat-ai-calls" label="AI Calls Today" value={d.ai_calls_today} icon={PhoneCall} sub={`${d.ai_calls_total} all-time`} />
        <Stat testid="stat-ai-texts" label="AI Texts Today" value={d.ai_texts_today} icon={MessageSquare} sub={`${d.ai_texts_total} all-time`} />
        <Stat testid="stat-bookings-today" label="Bookings Today" value={d.bookings_today} icon={Car} />
        <Stat testid="stat-outstanding" label="Outstanding Balances" value={`$${Number(d.outstanding_balance).toLocaleString()}`} icon={Wallet} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat testid="stat-conversion" label="Lead → Booking" value={`${d.conversion_rate}%`} icon={TrendingUp} />
        <Stat testid="stat-reviews" label="Reviews" value={d.reviews_count} icon={Star} sub={d.reviews_avg ? `${d.reviews_avg} ★ average` : "no ratings yet"} />
        <Stat testid="stat-fb-leads" label="Facebook Leads" value={channel("Facebook").leads} icon={Users} sub={`${channel("Facebook").booked} booked`} />
        <Stat testid="stat-tiktok-leads" label="TikTok Leads" value={channel("TikTok").leads} icon={Users} sub={`${channel("TikTok").booked} booked`} />
        <Stat testid="stat-ig-leads" label="Instagram Leads" value={channel("Instagram").leads} icon={Users} sub={`${channel("Instagram").booked} booked`} />
        <Stat testid="stat-google-leads" label="Google Leads" value={channel("Google").leads} icon={Users} sub={`${channel("Google").booked} booked`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h3 className="font-display font-bold text-white flex items-center gap-2 mr-auto"><Trophy size={17} className="text-[#F2B705]" /> Marketing Scoreboard</h3>
          <div className="flex gap-1.5">
            {RANKS.map((r) => (
              <button key={r.key} data-testid={`rank-by-${r.key}`} onClick={() => setRankBy(r.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${rankBy === r.key ? "bg-[#F2B705] text-black" : "bg-white/5 border border-white/10 text-white/60"}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
              <th className="py-2 pr-2 w-10">#</th>
              <th className="py-2 pr-3">Campaign / Channel</th>
              <th className="py-2 pr-3 text-right">Revenue</th>
              <th className="py-2 pr-3 text-right">Bookings</th>
              <th className="py-2 pr-3 text-right">ROI</th>
              <th className="py-2 text-right">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {ranked.slice(0, 15).map((e, i) => (
              <tr key={`${e.type}-${e.name}`} className="border-b border-white/5">
                <td className={`py-2.5 pr-2 font-black ${i === 0 ? "text-[#F2B705]" : "text-white/40"}`}>{i + 1}</td>
                <td className="py-2.5 pr-3">
                  <span className="text-white font-semibold">{e.name}</span>
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${e.type === "campaign" ? "bg-[#F2B705]/15 text-[#F2B705]" : "bg-white/5 text-white/50"}`}>{e.type}</span>
                </td>
                <td className="py-2.5 pr-3 text-right text-white font-semibold">${Number(e.revenue || 0).toLocaleString()}</td>
                <td className="py-2.5 pr-3 text-right text-white/70">{e.bookings}</td>
                <td className={`py-2.5 pr-3 text-right ${e.roi > 0 ? "text-green-400" : e.roi < 0 ? "text-red-400" : "text-white/40"}`}>{e.roi != null ? `${e.roi}%` : "—"}</td>
                <td className="py-2.5 text-right text-white/70">{e.conversion_rate != null ? `${e.conversion_rate}%` : "—"}</td>
              </tr>
            ))}
            {ranked.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-white/40">No campaigns yet — create one in the Campaigns tab.</td></tr>}
          </tbody>
        </table>
        </div>
        <ReferralLeaderboard token={token} />
      </div>
    </div>
  );
}
